import { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Filter, Download, Clock, TrendingUp, Users, FileText } from 'lucide-react';
import { toast } from 'sonner';
import {
  globalSearch,
  getSearchSuggestions,
  getRecentItems,
  trackItemAccess,
  getSearchStats,
  exportSearchResults,
  type SearchResult,
  type SearchFilters
} from '@/lib/search-utils';

const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>({});
  const [results, setResults] = useState<SearchResult[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [recentItems, setRecentItems] = useState<SearchResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const stats = useMemo(() => getSearchStats(), []);

  // Load recent items on mount
  useEffect(() => {
    setRecentItems(getRecentItems(8));
  }, []);

  // Handle search input
  useEffect(() => {
    if (query.length >= 2) {
      setSuggestions(getSearchSuggestions(query, 8));
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [query]);

  // Perform search
  const handleSearch = (searchQuery: string = query) => {
    if (!searchQuery.trim()) {
      setResults([]);
      toast.error('Sila masukkan kata kunci pencarian');
      return;
    }

    const searchResults = globalSearch(searchQuery, filters);
    setResults(searchResults);
    setShowSuggestions(false);

    if (searchResults.length === 0) {
      toast.info(`Tiada hasil ditemui untuk "${searchQuery}"`);
    } else {
      toast.success(`${searchResults.length} hasil ditemui`);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setShowSuggestions(false);
    setTimeout(() => handleSearch(suggestion), 0);
  };

  const handleResultClick = (result: SearchResult) => {
    trackItemAccess(result);
    toast.success(`${result.title} ditambahkan ke recent items`);
  };

  const handleExportResults = () => {
    if (results.length === 0) {
      toast.error('Tiada hasil untuk dieksport');
      return;
    }

    const csv = exportSearchResults(results, 'csv');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `search-results-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Hasil carian dieksport');
  };

  const getTypeIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'customer':
        return <Users size={16} className="text-blue-600" />;
      case 'project':
        return <TrendingUp size={16} className="text-purple-600" />;
      case 'invoice':
        return <FileText size={16} className="text-green-600" />;
      default:
        return <FileText size={16} className="text-slate-600" />;
    }
  };

  const getTypeLabel = (type: SearchResult['type']) => {
    const labels: Record<SearchResult['type'], string> = {
      customer: 'Pelanggan',
      project: 'Projek',
      invoice: 'Invois',
      quote: 'Sebutharga',
      cost: 'Kos',
      report: 'Laporan',
      visit: 'Lawatan',
      image: 'Gambar'
    };
    return labels[type];
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Global Search</h1>
        <p className="text-slate-600 mt-1">Cari merentasi semua modul dalam sistem</p>
      </div>

      {/* Search Box */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Input
                type="text"
                placeholder="Cari pelanggan, projek, invois, atau apa-apa..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="border-slate-300"
              />

              {/* Suggestions Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-lg shadow-lg z-50">
                  {suggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="w-full text-left px-4 py-2 hover:bg-slate-100 border-b border-slate-100 last:border-b-0 flex items-center gap-2"
                    >
                      <Search size={16} className="text-slate-400" />
                      <span className="text-sm text-slate-700">{suggestion}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Button
              onClick={() => handleSearch()}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2 px-6"
            >
              <Search size={18} />
              <span className="font-semibold">Cari</span>
            </Button>

            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="gap-2"
            >
              <Filter size={18} />
              <span className="font-semibold">Filter</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Dari Tarikh
                </label>
                <Input
                  type="date"
                  value={filters.dateFrom || ''}
                  onChange={(e) =>
                    setFilters({ ...filters, dateFrom: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Ke Tarikh
                </label>
                <Input
                  type="date"
                  value={filters.dateTo || ''}
                  onChange={(e) =>
                    setFilters({ ...filters, dateTo: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nilai Min (RM)
                </label>
                <Input
                  type="number"
                  placeholder="0"
                  value={filters.minValue || ''}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      minValue: e.target.value ? Number(e.target.value) : undefined
                    })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nilai Max (RM)
                </label>
                <Input
                  type="number"
                  placeholder="0"
                  value={filters.maxValue || ''}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      maxValue: e.target.value ? Number(e.target.value) : undefined
                    })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Status
                </label>
                <select
                  value={filters.status || ''}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      status: e.target.value || undefined
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Semua Status</option>
                  <option value="Selesai">Selesai</option>
                  <option value="Dalam Proses">Dalam Proses</option>
                  <option value="Tender">Tender</option>
                  <option value="Aktif">Aktif</option>
                  <option value="Tidak Aktif">Tidak Aktif</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <Button
                onClick={() => handleSearch()}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Apply Filters
              </Button>
              <Button
                onClick={() => {
                  setFilters({});
                  setResults([]);
                }}
                variant="outline"
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Stats */}
      {results.length === 0 && query === '' && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-slate-900">{stats.totalRecords}</p>
              <p className="text-xs text-slate-600 mt-1">Total Records</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-blue-600">{stats.totalCustomers}</p>
              <p className="text-xs text-slate-600 mt-1">Pelanggan</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-purple-600">{stats.totalProjects}</p>
              <p className="text-xs text-slate-600 mt-1">Projek</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-green-600">{stats.totalInvoices}</p>
              <p className="text-xs text-slate-600 mt-1">Invois</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-orange-600">{stats.totalQuotations}</p>
              <p className="text-xs text-slate-600 mt-1">Sebutharga</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-red-600">{stats.totalCosts}</p>
              <p className="text-xs text-slate-600 mt-1">Kos</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-indigo-600">{stats.totalReports}</p>
              <p className="text-xs text-slate-600 mt-1">Laporan</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-cyan-600">{stats.totalVisits}</p>
              <p className="text-xs text-slate-600 mt-1">Lawatan</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Items */}
      {results.length === 0 && query === '' && recentItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock size={20} />
              Baru-Baru Ini
            </CardTitle>
            <CardDescription>Item yang anda akses baru-baru ini</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {recentItems.map((item, idx) => (
                <div
                  key={idx}
                  onClick={() => handleResultClick(item)}
                  className="p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer"
                >
                  <div className="flex items-center gap-2 mb-2">
                    {getTypeIcon(item.type)}
                    <span className="text-xs font-semibold text-slate-500 uppercase">
                      {getTypeLabel(item.type)}
                    </span>
                  </div>
                  <p className="font-medium text-slate-900 text-sm">{item.title}</p>
                  {item.subtitle && (
                    <p className="text-xs text-slate-600 mt-1">{item.subtitle}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Results */}
      {results.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle>Hasil Carian ({results.length})</CardTitle>
                <CardDescription>
                  {query && `Hasil untuk "${query}"`}
                </CardDescription>
              </div>
              <Button
                onClick={handleExportResults}
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                size="sm"
              >
                <Download size={16} />
                Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {results.map((result, idx) => (
                <div
                  key={idx}
                  onClick={() => handleResultClick(result)}
                  className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition"
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getTypeIcon(result.type)}
                        <span className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                          {getTypeLabel(result.type)}
                        </span>
                        <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden ml-auto">
                          <div
                            className="h-full bg-blue-600"
                            style={{ width: `${result.relevance}%` }}
                          ></div>
                        </div>
                      </div>

                      <h3 className="font-bold text-slate-900 text-lg">{result.title}</h3>

                      {result.subtitle && (
                        <p className="text-sm text-slate-600 mt-1">{result.subtitle}</p>
                      )}

                      <div className="flex gap-4 mt-2 text-xs text-slate-500">
                        {result.value && (
                          <span>RM {result.value.toFixed(2)}</span>
                        )}
                        {result.date && (
                          <span>{new Date(result.date).toLocaleDateString('ms-MY')}</span>
                        )}
                        {result.status && (
                          <span className="px-2 py-0.5 rounded bg-slate-100">{result.status}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Results */}
      {results.length === 0 && query !== '' && (
        <Card>
          <CardContent className="pt-12 text-center pb-12">
            <Search size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-slate-600 text-lg">Tiada hasil ditemui</p>
            <p className="text-slate-500 text-sm mt-2">
              Cuba dengan kata kunci yang berbeza atau gunakan filter
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GlobalSearch;
